import { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";

// Default images with objectPosition for focus
const defaultImages = [
  { src: "/stockphotos/Man-in-Orange-Jacket-in-Industrial-Setting.webp", alt: "Industrial Worker", objectPosition: "center 30%" },
  { src: "/stockphotos/Chef-in-Pink-Uniform.webp", alt: "Chef at Work", objectPosition: "center 40%" },
  { src: "/stockphotos/Construction-Workers-at-Site.webp", alt: "Construction Team", objectPosition: "center 50%" },
  { src: "/stockphotos/Smiling-Woman-in-Factory.webp", alt: "Factory Worker", objectPosition: "center 35%" },
  { src: "/stockphotos/Culinary-Teamwork-Scene.webp", alt: "Culinary Team", objectPosition: "center 45%" },
  { src: "/stockphotos/Confident-Construction-Worker.webp", alt: "Confident Worker", objectPosition: "center 30%" },
  { src: "/stockphotos/Focused-Construction-Worker.webp", alt: "Focused Worker", objectPosition: "center 40%" },
  { src: "/stockphotos/Man-in-Automotive-Workshop.webp", alt: "Automotive Worker", objectPosition: "center 50%" },
  { src: "/stockphotos/Focused-Worker-in-Blue.webp", alt: "Blue Uniform Worker", objectPosition: "center 35%" },
];

const ImageSequenceScroller = ({ images = defaultImages, interval = 5000, style }) => {
  const maxFrames = Math.min(images.length, 65);
  const [frame, setFrame] = useState(0);
  const [transitionClass, setTransitionClass] = useState("fade");
  const timerRef = useRef(null);

  // Preload images
  useEffect(() => {
    if (typeof window !== "undefined") {
      images.slice(0, 65).forEach((img) => {
        const image = new window.Image();
        image.src = img.src;
      });
    }
  }, [images]);

  // Random transition types
  const transitions = ["fade", "slide-left", "slide-right", "scale"];

  // Auto-loop slideshow with random transitions
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const randomTransition = transitions[Math.floor(Math.random() * transitions.length)];
      setTransitionClass(randomTransition);
      setFrame((prev) => (prev + 1) % maxFrames);
    }, interval);

    return () => clearInterval(timerRef.current); // Cleanup on unmount
  }, [maxFrames, interval]);

  // Memoize current image
  const currentImage = useMemo(() => images[frame] || images[0], [images, frame]);

  return (
    <div className="image-sequence-scroller" style={{ ...style }}>
      <img
        src={currentImage.src}
        alt={currentImage.alt || "Carousel Image"}
        loading="lazy"
        className={`image-sequence-overlay ${transitionClass}`}
        style={{ objectPosition: currentImage.objectPosition || "center" }}
        draggable={false}
      />
    </div>
  );
};

// Prop validation
ImageSequenceScroller.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
      objectPosition: PropTypes.string,
    })
  ),
  interval: PropTypes.number,
  style: PropTypes.object,
};

ImageSequenceScroller.defaultProps = {
  images: defaultImages,
  interval: 7000,
  style: {},
};

export default ImageSequenceScroller;
